import { useState, useEffect } from 'react';
import Chart from 'react-apexcharts';
import moment from 'moment';
import {
  Box,
  Typography,
  Stack,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@material-ui/core';
import { lodgeColors } from './theme';
import { Panel } from './Panel';
import { KpiCard } from './KpiCard';
import { occupancyTrend, monthlyFinance, bookings } from './lodgeData';
import { hlekweniUrl } from '../../../../utils/constants';

/* eslint-disable no-nested-ternary */
const statusColor = (s) =>
  s === '1'
    ? { bg: 'rgba(2, 255, 61, 0.15)', fg: lodgeColors.accent }
    : s === '2'
    ? { bg: 'rgba(253, 7, 7, 0.18)', fg: lodgeColors.moss }
    : { bg: '#EFE7D2', fg: '#3F4A3A' };

const financeOptions = {
  chart: { toolbar: { show: false }, fontFamily: 'Inter, sans-serif', stacked: false },
  colors: [lodgeColors.primary, lodgeColors.accent],
  plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
  dataLabels: { enabled: false },
  grid: { borderColor: '#DCD3BD', strokeDashArray: 3 },
  xaxis: {
    categories: monthlyFinance.map((d) => d.m),
    labels: { style: { colors: '#5A6A5C', fontSize: '12px' } },
    axisBorder: { show: false },
    axisTicks: { show: false }
  },
  yaxis: { labels: { style: { colors: '#5A6A5C', fontSize: '12px' } } },
  legend: { fontSize: '12px', labels: { colors: '#5A6A5C' } },
  tooltip: { theme: 'light' }
};
const financeSeries = [
  { name: 'Revenue', data: monthlyFinance.map((d) => d.revenue) },
  { name: 'Expenses', data: monthlyFinance.map((d) => d.expenses) }
];

const HospitalityOccupancyChart = () => {
  const [loading, setLoading] = useState(false);
  const [dataList, setDataList] = useState([
    {
      bookingMonth: '2025-01-01T00:00:00',
      totalAmount: 0.0
    },
    {
      bookingMonth: '2025-02-01T00:00:00',
      totalAmount: 0.0
    }
  ]);
  const [kpis, setKpis] = useState([]);
  const [bookingList, setBookList] = useState([]);
  const handleRefresh = () => getChartData();

  useEffect(() => {
    getChartData();
    getDashBoardSummaries();
    getBookingBalances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getChartData = () => {
    setLoading(true);
    const url = `${hlekweniUrl}/api/booking/GetMonthlyStats?period=2026-05-04`;
    fetch(url)
      .then((res) => res.json())
      .then(
        (result) => {
          setLoading(false);
          console.log('returned GetMonthlyStats Summary list', result.result);
          setDataList(result.result ?? []);
        },
        (error) => {
          setLoading(false);
          // notifyPopup(`Error in getChartData!: ${error}`, 'error');
        }
      );
  };

  const getDashBoardSummaries = () => {
    setLoading(true);
    const url = `${hlekweniUrl}/api/booking/getDashBoardSummaries?period=2026-05-04`;
    fetch(url)
      .then((res) => res.json())
      .then(
        (result) => {
          setLoading(false);
          console.log('returned getDashBoardSummaries Summary list', result.result);
          setKpis(
            result.result.map((item) => ({
              label: item.title,
              value: item.value,
              delta: item.per,
              trend: getTrend(item.per),
              hint: item.text
            }))
          );
        },
        (error) => {
          setLoading(false);
          // notifyPopup(`Error in getChartData!: ${error}`, 'error');
        }
      );
  };

  const getBookingBalances = () => {
    setLoading(true);
    const url = `${hlekweniUrl}/api/booking/getBookingBalances?period=2026-05-04`;
    fetch(url)
      .then((res) => res.json())
      .then(
        (result) => {
          setLoading(false);
          console.log('returned getBookingBalances Summary list', result.result);
          setBookList(result.result ?? []);
        },
        (error) => {
          setLoading(false);
          // notifyPopup(`Error in getChartData!: ${error}`, 'error');
        }
      );
  };

  const occupancyOptions = {
    chart: { toolbar: { show: false }, fontFamily: 'Inter, sans-serif', zoom: { enabled: false } },
    colors: [lodgeColors.primary],
    stroke: { curve: 'smooth', width: 2.5 },
    fill: {
      type: 'gradient',
      gradient: { shadeIntensity: 1, opacityFrom: 0.45, opacityTo: 0, stops: [0, 100] }
    },
    dataLabels: { enabled: false },
    grid: { borderColor: '#DCD3BD', strokeDashArray: 3 },
    xaxis: {
      categories: dataList.map((d) => moment(d.bookingMonth).format('MMM YYYY')),
      labels: { style: { colors: '#5A6A5C', fontSize: '12px' } },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: { labels: { style: { colors: '#5A6A5C', fontSize: '12px' } } },
    tooltip: { theme: 'light' }
  };
  const occupancySeries = [{ name: 'Occupancy', data: dataList.map((d) => d.totalAmount) }];

  const getTrend = (per) => {
    if (per > 0) return 'up';
    if (per < 0) return 'down';
    return 'neutral';
  };

  const formatDelta = (per) => {
    if (per > 0) return `+${per}%`;
    if (per < 0) return `${per}%`;
    return '0%';
  };

  const handleLoadingClose = () => {
    setLoading(false);
  };

  console.log('kpis are', kpis);
  return (
    <Stack spacing={3}>
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 4,
          boxShadow: '0 18px 40px -16px rgba(31,58,44,0.3)'
        }}
      >
        {/* <Box
        component="img"
        src={heroImg}
        alt="Hartwood Lodge at golden hour"
        sx={{ width: '100%', height: { xs: 224, md: 288 }, objectFit: 'cover', display: 'block' }}
      /> */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(90deg, rgba(31,58,44,0.92), rgba(31,58,44,0.55) 60%, transparent)'
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            p: 4,
            color: '#FBF7EE'
          }}
        >
          <Typography variant="overline" sx={{ opacity: 0.85, fontSize: 11 }}>
            Monthly Briefing
          </Typography>
          <Typography variant="h3" sx={{ mt: 1, maxWidth: 720, fontSize: { xs: 26, md: 36 } }}>
            A strong autumn — bookings up, gardens thriving, and the herd at full milk.
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', xl: 'repeat(4, 1fr)' },
          gap: 2
        }}
      >
        {kpis.map((k, i) => (
          <KpiCard key={k.label} {...k} index={i} />
        ))}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '2fr 1fr' }, gap: 3 }}>
        <Panel title="Monthly Occupancy" subtitle="Rooms filled vs. average daily rate">
          <Chart type="area" height={280} options={occupancyOptions} series={occupancySeries} />
        </Panel>

        <Panel title="6-Month Finance" subtitle="Revenue vs. expenses (R '000)">
          <Chart type="bar" height={280} options={financeOptions} series={financeSeries} />
        </Panel>
      </Box>

      <Panel title="Today's Front Desk" subtitle="Arrivals, in-house guests and upcoming stays">
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Name', 'Status', 'Check In', 'Check Out', '# Days ', 'Balance', 'Status'].map(
                  (h) => (
                    <TableCell
                      key={h}
                      sx={{
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        fontSize: 11,
                        color: 'text.secondary',
                        fontWeight: 500
                      }}
                    >
                      {h}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {bookingList.map((b) => {
                const c = statusColor(b.status);
                return (
                  <TableRow key={b.id} hover>
                    <TableCell
                      sx={{
                        fontFamily: 'ui-monospace, monospace',
                        fontSize: 12,
                        color: 'text.secondary'
                      }}
                    >
                      {b.accountName}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{b.booked}</TableCell>
                    {/* <TableCell sx={{ color: 'text.secondary' }}>{b.status}</TableCell> */}
                    <TableCell>{moment(b.checkInDate).format('YYYY-MM-DD')}</TableCell>
                    <TableCell>{moment(b.checkOutDate).format('YYYY-MM-DD')}</TableCell>
                    <TableCell>{b.noOfDays}</TableCell>
                    <TableCell>{b.balance}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={b.booked === 1 ? 'Checked In' : 'Checked Out'}
                        sx={{ bgcolor: c.bg, color: c.fg, fontWeight: 500, fontSize: 11 }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Panel>
    </Stack>
  );
};
export default HospitalityOccupancyChart;
